import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Mail, Building2, ShieldCheck, Loader2, AlertTriangle } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { SetPasswordSchema, type SetPasswordValues } from '../auth/LoginSchema';

// Legacy JS api module — typed via legacy-shims.d.ts.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import * as invitesApi from '@/api/invites';

interface InviteView {
  email: string;
  tenant_id: number | null;
  tenant_name?: string | null;
  roles: string[];
  note?: string | null;
  status: string;
  expires_at?: string | null;
  inviter_name?: string | null;
}

/**
 * Public landing page for an invitation, mounted at /invite/:token outside
 * the AppShell so unauthenticated users can reach it.
 *
 * The token is the only credential — server-side, GET /invites/{token} stamps
 * `opened_at` on first call so the admin can see "they clicked". The user then
 * chooses a permanent password here; POST .../accept commits it to Cognito
 * (AdminSetUserPassword) and we bounce them to /auth/login to sign in with it.
 */
export default function InviteLanding() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [invite, setInvite] = useState<InviteView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<SetPasswordValues>({
    resolver: zodResolver(SetPasswordSchema),
    defaultValues: { newPassword: '', confirmNewPassword: '' },
    mode: 'onTouched',
  });
  const { isSubmitting } = form.formState;

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setError('Invitation token missing from URL.');
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const data = (await invitesApi.getInviteByToken(token)) as unknown as InviteView;
        if (cancelled) return;
        setInvite(data);
      } catch (err) {
        if (cancelled) return;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const e = err as any;
        setError(
          e?.response?.data?.detail ||
            e?.message ||
            'This invitation could not be loaded.',
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const onSubmit = async (values: SetPasswordValues) => {
    if (!token) return;
    setSubmitError(null);
    try {
      await invitesApi.acceptInvite(token, values.newPassword);
      toast.success('Password set — please sign in with your new password.');
      navigate('/auth/login?from=invite', { replace: true });
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = err as any;
      setSubmitError(
        e?.response?.data?.detail ||
          e?.message ||
          'Failed to set your password. Please try again.',
      );
    }
  };

  // Once accepted/revoked/expired the token can't be used to set a password.
  const isActionable =
    invite != null &&
    !['accepted', 'revoked', 'expired'].includes((invite.status || '').toLowerCase());

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            You&apos;ve been invited
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading && (
            <div className="space-y-3">
              <Skeleton className="h-8 w-2/3" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-4/5" />
            </div>
          )}

          {!loading && error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {!loading && !error && invite && (
            <>
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Building2 className="h-4 w-4" />
                  Organisation
                </div>
                <div className="text-lg font-semibold">
                  {invite.tenant_name || `FPA ${invite.tenant_id ?? ''}`}
                </div>
              </div>

              <div className="space-y-1">
                <div className="text-sm text-muted-foreground">Invited email</div>
                <div className="font-medium">{invite.email}</div>
              </div>

              {invite.inviter_name && (
                <div className="space-y-1">
                  <div className="text-sm text-muted-foreground">Invited by</div>
                  <div>{invite.inviter_name}</div>
                </div>
              )}

              {invite.roles.length > 0 && (
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <ShieldCheck className="h-4 w-4" />
                    Role{invite.roles.length > 1 ? 's' : ''}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {invite.roles.map((r) => (
                      <Badge key={r} variant="secondary">
                        {r}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {invite.note && (
                <Alert>
                  <AlertDescription className="whitespace-pre-wrap">
                    {invite.note}
                  </AlertDescription>
                </Alert>
              )}

              {invite.expires_at && (
                <p className="text-xs text-muted-foreground">
                  Invitation expires{' '}
                  {new Date(invite.expires_at).toLocaleString(undefined, {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })}
                  .
                </p>
              )}

              {isActionable ? (
                <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="space-y-4 border-t pt-4"
                  >
                    <p className="text-sm text-muted-foreground">
                      Choose a password to activate your account.
                    </p>

                    {submitError && (
                      <Alert variant="destructive">
                        <AlertDescription>{submitError}</AlertDescription>
                      </Alert>
                    )}

                    <FormField
                      control={form.control}
                      name="newPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>New password</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              type="password"
                              autoComplete="new-password"
                              autoFocus
                              disabled={isSubmitting}
                            />
                          </FormControl>
                          <FormDescription>
                            At least 12 characters with an upper- and lowercase
                            letter, a number and a symbol.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="confirmNewPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Confirm password</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              type="password"
                              autoComplete="new-password"
                              disabled={isSubmitting}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button type="submit" className="w-full" disabled={isSubmitting}>
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Setting password…
                        </>
                      ) : (
                        'Set password & activate'
                      )}
                    </Button>
                  </form>
                </Form>
              ) : (
                <Alert>
                  <AlertDescription>
                    This invitation has already been used or is no longer valid.{' '}
                    <a href="/auth/login" className="font-medium underline">
                      Go to sign in
                    </a>
                    .
                  </AlertDescription>
                </Alert>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
